import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  usePrevious,
} from "@chakra-ui/react";
import { BatchItem } from "@rpldy/uploady";
import { useQueryClient } from "@tanstack/react-query";
import {
  documentStatusUploadCompleteUpdate,
  useDocumentRetrieve,
} from "gen/endpoints/documents/documents";
import {
  getInternalEventImportExportTableDataRetrieveQueryKey,
  useInternalEventImportCreate,
} from "gen/endpoints/internal/internal";
import { DocumentCreateResponseData } from "gen/model/documentCreateResponseData";
import { DocumentStateMachineFieldsEnum } from "gen/model/documentStateMachineFieldsEnum";
import { DocumentTypeEnum } from "gen/model/documentTypeEnum";
import { EventImportCreateDataRequest } from "gen/model/eventImportCreateDataRequest";
import { get } from "lodash-es";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import Form, { FormHandle } from "shared/components/form/Form";
import { FileUpload } from "shared/components/upload/FileUpload";
import { FileUploadHandle } from "shared/components/upload/FileUploadController";
import { uploadAcceptJson } from "shared/components/upload/FileUploadUtils";
import { rootRoute } from "shared/navigation/Routes";

export interface InternalTemplateImportModalProps {
  onCloseComplete(): void;
}

export const InternalTemplateImportModal: React.FC<
  InternalTemplateImportModalProps
> = ({ onCloseComplete }) => {
  const { t } = useTranslation(["internal", "common"]);
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const { mutateAsync: createEventTemplateImport } =
    useInternalEventImportCreate();

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const uploadRef = useRef<FileUploadHandle>(null);
  const form = useForm<EventImportCreateDataRequest>();
  const formRef = useRef<FormHandle>(null);

  const onSubmit = async (data: EventImportCreateDataRequest) => {
    const templateImport = await createEventTemplateImport({
      data,
    });
    await queryClient.invalidateQueries({
      queryKey: getInternalEventImportExportTableDataRetrieveQueryKey({
        import_export_type: "import",
      }),
    });
    navigate(
      rootRoute({})
        .internal({})
        .shadowTemplate({
          templateVersionId: templateImport.event_template_version_id,
        })
        .template({}).$,
    );
  };

  const documentId = form.watch("document_id");
  const { data } = useDocumentRetrieve(documentId, {
    query: {
      enabled: !!documentId,
      refetchInterval: 1000,
    },
  });

  const onItemsChanged = useCallback(
    (items: BatchItem[]) => {
      if (items.length === 0) {
        form.setValue("document_id", "");
      }
    },
    [form],
  );

  const onItemFinished = async (item: BatchItem) => {
    // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
    const document: DocumentCreateResponseData = get(item, "document")!;
    await documentStatusUploadCompleteUpdate(document.id);
    setErrorMessage(undefined);
    form.setValue("document_id", document.id);
    await formRef.current?.submit();
  };

  const prevState = usePrevious(data?.state);

  useEffect(() => {
    if (data && prevState !== data.state) {
      if (data.state === DocumentStateMachineFieldsEnum.successful_ingestion) {
        onCloseComplete();
      } else {
        uploadRef.current?.clear();
        setErrorMessage(data.error);
        form.setValue("document_id", "");
      }
    }
  }, [data, prevState, onCloseComplete, form]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <Form
          formRef={formRef}
          form={form}
          onSubmit={onSubmit}
          onSuccess={onClose}
          successMessage={t("internal:templateTabs.importTemplate.success")}
        >
          <ModalHeader>
            {t("internal:templateTabs.importTemplate.title")}
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody>
            <Text mb={4}>
              {t("internal:templateTabs.importTemplate.description")}
            </Text>
            <Box mb="16px">
              <FileUpload
                documentType={DocumentTypeEnum.event_template}
                accept={uploadAcceptJson}
                onItemFinished={onItemFinished}
                onItemsChanged={onItemsChanged}
                handleRef={uploadRef}
              />
            </Box>
            {errorMessage && (
              <Alert
                status="error"
                mt="16px"
                data-ls-enabled={true}
                data-public={true}
              >
                <AlertIcon />
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            <FormCancelButton onClick={onClose} autoFocus={true} />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
